import { useCallback } from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function DialogRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const isOpen = element.variant === "open"
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        dispatchEvent(element.id, "close", {})
      }
    },
    [element.id, dispatchEvent],
  )

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
            element.className,
          )}
          data-testid={`dialog-${element.id}`}
        >
          {text && (
            <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
              {text}
            </DialogPrimitive.Title>
          )}
          {element.children?.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
